import { setInteractionSoundMuted } from "@healthos/ui";
import { bind, play, type SoundName, setEnabled } from "cuelume";
import { useSyncExternalStore } from "react";

/**
 * Interaction sound layer (cuelume). A single module store, not a React
 * provider: the enabled state, its persistence, and the one-time cuelume
 * `bind()` all live here, so any component reads the same truth via
 * `useSound()` with no provider to wrap (tests render unwrapped and still
 * pass). One mute gates BOTH paths cuelume plays through:
 *   - declarative chrome: `data-cuelume-*` attributes on primitives (Button
 *     press, Switch toggle), wired globally by `bind()`.
 *   - imperative moments: `playSound("success")` at semantic beats (a step
 *     completed, a save failed).
 *
 * cuelume synthesizes every sound live via the Web Audio API (no asset files)
 * and is a safe no-op before the first user gesture unlocks the AudioContext,
 * so a press/toggle is always the first sound heard.
 */

const STORAGE_KEY = "healthos-sound";

function readPref(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "0") return false;
    if (v === "1") return true;
  } catch {
    // localStorage unavailable; default on.
  }
  return true;
}

function hasStoredPref(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "0" || v === "1";
  } catch {
    return false;
  }
}

function writePref(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    // localStorage unavailable; the preference just won't persist.
  }
}

let enabled = readPref();
let bound = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/**
 * Idempotent boot: wire the declarative `data-cuelume-*` delegation once and
 * apply the persisted mute state to cuelume. Called from the App root effect.
 * Safe to call repeatedly and safe when `window` is absent.
 */
export function initSound(): void {
  if (bound || typeof window === "undefined") return;
  bound = true;
  try {
    bind();
    installDefaultPressDelegation();
    setEnabled(enabled);
    // The hold-to-complete control synthesizes its own Web Audio (filling ticks
    // + completion chord), separate from cuelume; keep it under the same mute.
    setInteractionSoundMuted(!enabled);
  } catch {
    // Web Audio unavailable (old browser, locked-down context); sound just
    // never plays. Never let the audio layer break the app.
  }
}

// The taxonomy's default cue, applied globally so EVERY actionable control makes
// a press without being wired one by one. This is not DOM sniffing for special
// sounds: a control that declares a cue (any data-cuelume-* attribute, emitted
// by interactionSoundProps) is left entirely to cuelume's bind(); this only
// supplies the declared default ("action" -> press) for a plain button/link.
const ACTIONABLE_SELECTOR =
  'button, [role="button"], a[href], summary, input[type="button"], input[type="submit"], input[type="reset"]';
// Elements whose click already resolves a cue elsewhere (an explicit taxonomy
// declaration) or that are not "actions" (selection/state controls sound only
// when they declare it, and stay silent when active). Excluded from the default.
const DECLARED_CUE_ATTRS = [
  "data-cuelume-press",
  "data-cuelume-toggle",
  "data-cuelume-hover",
  "data-cuelume-release",
];
const SELECTION_ROLES = new Set([
  "tab",
  "switch",
  "radio",
  "checkbox",
  "menuitemcheckbox",
  "menuitemradio",
  "option",
]);

/** Whether an actionable element takes the taxonomy's default press cue.
 *  Exported for tests; the delegation below is the only runtime caller. */
export function takesDefaultPress(el: Element): boolean {
  for (const attr of DECLARED_CUE_ATTRS) {
    if (el.hasAttribute(attr)) return false;
  }
  // `data-silent` opts an element or its subtree out (controls that own their
  // own audio, e.g. hold-to-complete, or that play an imperative cue on click).
  if (el.closest("[data-silent]")) return false;
  if ((el as HTMLButtonElement).disabled) return false;
  if (el.getAttribute("aria-disabled") === "true") return false;
  const role = el.getAttribute("role");
  if (role && SELECTION_ROLES.has(role)) return false;
  if (el.hasAttribute("aria-selected") || el.hasAttribute("aria-checked")) return false;
  const current = el.getAttribute("aria-current");
  if (current && current !== "false") return false;
  return true;
}

let delegationInstalled = false;
function installDefaultPressDelegation(): void {
  if (delegationInstalled || typeof document === "undefined") return;
  delegationInstalled = true;
  document.addEventListener(
    "pointerdown",
    (event) => {
      const target = event.target as Element | null;
      const el = target?.closest?.(ACTIONABLE_SELECTOR) ?? null;
      if (el && takesDefaultPress(el)) play("press");
    },
    { passive: true, capture: true },
  );
}

export function isSoundEnabled(): boolean {
  return enabled;
}

/**
 * Adopt the server-persisted preference (onboarding profile `sound`) when this
 * browser holds no explicit choice of its own. The desktop sidecar renders the
 * app in a cross-origin iframe whose localStorage does not survive a relaunch
 * (WKWebView partitions third-party storage), so the profile value is what
 * makes a mute stick overnight. A "0"/"1" already in localStorage means the
 * member chose on this device, and that choice was itself patched to the
 * profile, so it wins for the session. Deliberately does not write
 * localStorage: the key keeps meaning "an explicit choice made here".
 */
export function hydrateSoundPref(serverOn: boolean | null | undefined): void {
  if (typeof serverOn !== "boolean") return;
  if (hasStoredPref()) return;
  if (serverOn === enabled) return;
  enabled = serverOn;
  try {
    setEnabled(serverOn);
    setInteractionSoundMuted(!serverOn);
  } catch {
    // Web Audio unavailable; the state still updated for subscribers.
  }
  emit();
}

export function setSoundEnabled(on: boolean): void {
  if (on === enabled) return;
  enabled = on;
  writePref(on);
  try {
    setEnabled(on);
    setInteractionSoundMuted(!on);
  } catch {
    // ignore; state + persistence still updated.
  }
  emit();
}

/**
 * Fire a named interaction sound. No-op when muted (cuelume gates internally)
 * and when Web Audio is unavailable. cuelume's play() is synchronous and self
 * guarding, so this just adds a belt-and-braces try/catch.
 */
export function playSound(name: SoundName): void {
  try {
    play(name);
  } catch {
    // A failed sound must never surface to the user.
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Subscribe to enabled-state changes. Exposed for tests; components use `useSound`. */
export const subscribeSound = subscribe;

export interface SoundControls {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  play: (name: SoundName) => void;
}

/** Reactive access to the sound state. Works with or without any provider. */
export function useSound(): SoundControls {
  const value = useSyncExternalStore(subscribe, isSoundEnabled, isSoundEnabled);
  return { enabled: value, setEnabled: setSoundEnabled, play: playSound };
}

export type { SoundName };
